import {
  Button,
  FileUploader,
  Input,
  Switch,
  Textarea,
  ToastMessage,
  Multiselect,
  Breadcrumbs
} from '@cokitchen/cokitchen-components';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ROUTE_KEYS, STORAGE_KEYS } from 'utils/constants';
import './index.scss';
import SelectDropdown from 'components/base/select-dropdown';
import { RmxArrowBackIcon } from 'cokitchen-icons';
import useDashboardContext from 'contexts/dashboard.context';
import useAxios from 'hooks/useAxios';
import { toast } from 'react-toastify';
import {
  generateKey,
  getCokitchenName,
  handleApiError,
  isEndTimeBeforeStartTime,
  isSameDay,
  isTodayAndTimePassed
} from 'utils/functions';
import { useExploreContext } from 'contexts/explore.context';
import { ExplorePostType } from 'models/explore-posts.model';
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar-icon.svg';
import { ReactComponent as TimeIcon } from 'assets/icons/time-icon.svg';
import moment from 'moment';
import DatePicker from 'components/base/date-picker';
import { MultiselectItem } from '@cokitchen/cokitchen-components/dist/form/multiselect/types';
import PageHeaderText from 'components/base/page-header-text';
import { CreateExplorePostType } from 'models/posts.model';
import { initialExplorePostData } from 'contexts/explore.context/context';
import TimeSelector, { TimeSelectorType } from 'components/base/time-selector';
import { DeleteIcon } from 'cokitchen-icons';
import PageLoader from 'components/base/page-loader';
import { ENUM_DISPLAY_MODE } from 'utils/enums';

const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) as string;
type Props = {
  contentType: 'video' | 'photos';
  formState: CreateExplorePostType;
  setFormState: (value: CreateExplorePostType) => void;
  handleSubmit?: () => void;
};
const ContentUpload = ({ contentType, formState, setFormState }: Props) => {
  const uploadUrl = 'https://file-api.getfoodcourt.com/upload-explore-video';
  const [showStartDateModal, setShowStartDateModal] = useState(false);
  const [showEndDateModal, setShowEndDateModal] = useState(false);
  const isPhotoContent = contentType === 'photos';
  const navigate = useNavigate();
  const { cokitchens } = useDashboardContext();
  const [searchParams] = useSearchParams();

  const { isFormValid, brandData, mealData, handleFormState, contentUrl, setContentUrl } =
    useExploreContext();
  const [brandOptions, setBrandOptions] = useState<MultiselectItem[]>([]);
  const [mealOptions, setMealOptions] = useState<MultiselectItem[]>([]);

  useEffect(() => {
    setBrandOptions(
      brandData.map((brand) => ({
        text: `${brand?.name} - ${brand?.cokitchen?.name}`,
        value: brand?.id
      }))
    );
  }, [brandData]);

  useEffect(() => {
    setMealOptions(
      mealData.map((meal) => ({
        text: `${meal?.name} - ${meal?.brand?.name} - ${getCokitchenName(meal, brandData)}`,
        value: meal?.id
      }))
    );
  }, [mealData]);

  const isEditRoute = JSON.parse(searchParams.get('edit') as string);
  const postId = searchParams.get('postId');

  const { fetchData: mutateExplorePostFn, loading: isMutating } = useAxios({
    url: `internal/marketing/explore-posts`,
    isInternal: true,
    method: 'post'
  });

  const { fetchData: updateExplorePostFn, loading: isUpdating } = useAxios({
    url: `internal/marketing/explore-posts/${postId}`,
    isInternal: true,
    method: 'patch'
  });

  const { fetchData, loading, response } = useAxios<{ data: ExplorePostType }>({
    url: `internal/marketing/explore-posts/${postId}`,
    isInternal: true
  });

  useEffect(() => {
    if (isEditRoute && postId) fetchData();
  }, []);
  useEffect(() => {
    if (response?.data) handleFormState(response.data);
  }, [response?.data]);

  const handleSubmitExplorePost = async () => {
    if (
      isTodayAndTimePassed(formState.startDate, formState.startTime?.timeWithColon || '00:00') ||
      isTodayAndTimePassed(formState.endDate, formState.endTime?.timeWithColon) ||
      (isSameDay(formState.startDate, formState.endDate) &&
        isEndTimeBeforeStartTime(
          formState.startTime?.timeWithColon,
          formState.endTime?.timeWithColon
        ))
    ) {
      toast(<ToastMessage type='error' message={'Invalid time selected'} />);
      return;
    }
    const brandDataToServer =
      formState?.brand
        ?.map((brandId) => {
          const matchedBrand = brandData?.find((serverBrand) => serverBrand?.id === brandId);
          return matchedBrand
            ? {
                id: matchedBrand?.id,
                name: matchedBrand?.name,
                cokitchen_id: matchedBrand?.cokitchen_id,
                images: [matchedBrand?.images?.[0]]
              }
            : null;
        })
        ?.filter(Boolean) ?? [];

    const mealDataToServer =
      formState?.meal
        ?.map((mealId) => {
          const matchedMeal = mealData?.find((serverMeal) => serverMeal?.id === mealId);
          return matchedMeal
            ? {
                id: matchedMeal?.id,
                name: matchedMeal?.name,
                brand_id: matchedMeal?.brand_id,
                amount: matchedMeal?.amount,
                images: [matchedMeal?.images?.[0]]
              }
            : null;
        })
        ?.filter(Boolean) ?? [];
    const videoUrl = contentUrl?.[0];
    const videoFormat = videoUrl.split('.')?.pop()?.toLowerCase() || '';
    const dataToServer = {
      photos: contentUrl.filter((url) => url !== '') || [],
      ...(contentType === 'photos' && {
        cover_image: contentUrl?.[0]
      }),
      caption: formState?.caption,
      ...(isEditRoute
        ? { cokitchen_id: formState?.cokitchenLocation?.[0] }
        : { cokitchen_ids: formState?.cokitchenLocation }),
      brands: brandDataToServer,
      meals: mealDataToServer,
      start_date: moment(formState?.startDate).format('YYYY-MM-DD'),
      start_time: formState?.startTime?.timeWithColon || '00:00',
      type: contentType,
      is_promo_post: formState.promoPost,
      display_mode: formState.display_mode,
      cta: formState.cta,
      video: {
        videoUrl,
        videoFormat
      },
      ...(formState.hasEndTime &&
        formState?.endDate && { end_date: moment(formState?.endDate).format('YYYY-MM-DD') }),
      ...(formState.hasEndTime &&
        formState?.endTime?.timeWithColon && {
          end_time: formState?.endTime?.timeWithColon
        })
    };
    const newObj = Object.fromEntries(
      Object.entries(dataToServer).filter(([key]) => (isPhotoContent ? key !== 'video' : 'photos'))
    );

    try {
      if (isEditRoute && postId) {
        await updateExplorePostFn({}, { ...newObj });
      } else {
        await mutateExplorePostFn({}, { ...newObj });
      }

      toast(<ToastMessage message='Successfully' type='success' />);
      navigate(ROUTE_KEYS.EXPLORE);
    } catch (err) {
      handleApiError(err);
    } finally {
      setFormState(initialExplorePostData);
      setContentUrl([]);
    }
  };

  const handleChange = ({
    name,
    value
  }: {
    name: keyof CreateExplorePostType;
    value: string | boolean | string[] | Date | TimeSelectorType | undefined | null;
  }) => {
    setFormState({ ...formState, [name]: value });
  };

  const handleSetImageUrls = (urls: string[]) => {
    setContentUrl(urls);
  };
  const breadcrumbs = [
    { name: 'Explore', path: ROUTE_KEYS.EXPLORE },
    { name: `${isEditRoute ? 'Edit ' : ''}${contentType} upload`, path: '' }
  ];

  const handleDeleteImage = (index: number) => {
    const images = [...contentUrl];
    images[index] = '';
    setContentUrl(images);
  };

  const handleUpdateImage = (url: string, index: number) => {
    const images = [...contentUrl];
    images[index] = url;
    setContentUrl(images);
  };

  const MAX_PHOTO_SIZE = 25 * 1024 * 1024;
  const MAX_VIDEO_SIZE = 100 * 1024 * 1024;

  const displayModeOptions = [
    {
      id: ENUM_DISPLAY_MODE.MEAL_CARD,
      name: ENUM_DISPLAY_MODE.MEAL_CARD
    },
    {
      id: ENUM_DISPLAY_MODE.CTA,
      name: ENUM_DISPLAY_MODE.CTA.toUpperCase()
    }
  ];

  const handleDisplayModeChange = (val: { id: string }) =>
    setFormState({
      ...formState,
      display_mode: val?.id,
      ...(val?.id === ENUM_DISPLAY_MODE.MEAL_CARD ? { cta: '' } : { cta: 'Order now' })
    });

  const cokitchenOptions = cokitchens?.map((kitchen) => ({
    value: kitchen?.id,
    text: kitchen?.name
  }));

  return (
    <section className='w-full'>
      {showStartDateModal && (
        <DatePicker
          isOpen={showStartDateModal}
          onClose={() => setShowStartDateModal(false)}
          title='Select Start Date'
          handleDateSelect={(value) => {
            setFormState({
              ...formState,
              startDate: value,
              endDate: undefined,
              endTime: null
            });
          }}
          minDate={new Date()}
        />
      )}
      {showEndDateModal && (
        <DatePicker
          isOpen={showEndDateModal}
          onClose={() => setShowEndDateModal(false)}
          title='Select Expiry Date'
          handleDateSelect={(value) => handleChange({ name: 'endDate', value })}
          minDate={formState.startDate || new Date()}
        />
      )}
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      <div className='mt-8'>
        <PageHeaderText
          title={`${isEditRoute ? 'Edit ' : ''}${contentType} upload`}
          description='Lorem ipsum dolor sit amet, consectetur adipisicing elit. Minus, esse at eveniet illo
          pariatur'
        />
      </div>
      {isEditRoute && loading ? (
        <PageLoader />
      ) : (
        <form className='mt-12 space-y-6' onSubmit={(e) => e.preventDefault()}>
          {isEditRoute && formState.type === 'photos' ? (
            <div className='border border-[#EAEBEB] rounded-xl mt-6 '>
              <div className='px-6 py-[18px]'>
                <p className=' text-[#1A1A1A] text-sm  font-medium '>
                  Edit {contentType === 'photos' ? 'photos' : 'video'}
                </p>
                <p className='text-[#595959] text-xs'>Upload up to 6 images</p>
              </div>
              <hr className='text-[#EAEBEB]' />
              <div className='p-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4'>
                {contentUrl?.map((url: string, index: number) => (
                  <div key={`${url}${index}`} className='relative group'>
                    <FileUploader
                      maxFileSize={MAX_PHOTO_SIZE}
                      token={token}
                      fileUrls={url ? [url] : []}
                      setFileUrls={(urls) => handleUpdateImage(urls[0], index)}
                      className='h-[160px] m-0'
                    />
                    {url ? (
                      <div
                        onClick={() => handleDeleteImage(index)}
                        className='absolute top-[14px] right-5 cursor-pointer opacity-0 group-hover:opacity-100 '>
                        <DeleteIcon className='text-[#fff]' />
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <fieldset>
              <label htmlFor='file' className='mb-2  font-medium text-sm text-gray-38'>
                Add {contentType === 'photos' ? 'photos (Upload up to 6 images)' : 'video'}
              </label>
              <FileUploader
                token={token}
                fileType={isPhotoContent ? 'image' : 'video'}
                maxFileSize={isPhotoContent ? MAX_PHOTO_SIZE : MAX_VIDEO_SIZE}
                {...(!isPhotoContent && { apiUrl: uploadUrl })}
                {...(isPhotoContent && { isMultiple: true, maxNumberOfFiles: 6 })}
                fileUrls={contentUrl?.length ? contentUrl : []}
                className='!mt-2 !mb-0'
                setFileUrls={isPhotoContent ? handleSetImageUrls : (video) => setContentUrl(video)}
              />
            </fieldset>
          )}
          <fieldset className='flex flex-col gap-1 '>
            <p className='mb-2 font-medium text-sm text-gray-38'>Caption</p>
            <Textarea
              className='!resize-none '
              design='bordered'
              value={formState.caption}
              onChange={(e) => handleChange({ name: 'caption', value: e.target.value })}
            />
          </fieldset>
          <fieldset>
            <p className='mb-2 font-medium text-sm text-gray-38'>Location</p>
            <Multiselect
              {...{ isSingle: isEditRoute }}
              key={generateKey(cokitchenOptions)}
              placeholder='Select co-kitchen(s)'
              values={formState?.cokitchenLocation}
              options={cokitchenOptions}
              onChange={(val) =>
                setFormState({
                  ...formState,
                  cokitchenLocation: val,
                  brand: [],
                  meal: []
                })
              }
              hasSearch
              filterResults
            />
          </fieldset>
          <fieldset>
            <p className='mb-2 font-medium text-sm text-gray-38'>Brands</p>
            <Multiselect
              key={generateKey(brandOptions)}
              placeholder='Select brand'
              values={formState.brand}
              options={brandOptions}
              onChange={(val) =>
                setFormState({
                  ...formState,
                  brand: val,
                  meal: []
                })
              }
              hasSearch
              filterResults
            />
          </fieldset>
          <fieldset>
            <p className='mb-2 font-medium text-sm text-gray-38'>Meals</p>
            <Multiselect
              key={generateKey(mealOptions)}
              placeholder='Select Meals'
              values={formState.meal}
              options={mealOptions}
              onChange={(val) => handleChange({ name: 'meal', value: val })}
              hasSearch
              filterResults
            />
          </fieldset>
          <fieldset>
            <p className='mb-2 font-medium text-sm text-gray-38'>Display Mode</p>
            <SelectDropdown
              value={formState?.display_mode}
              setValue={handleDisplayModeChange}
              placeholder='Select display mode'
              options={displayModeOptions}
            />
          </fieldset>
          {formState.display_mode === ENUM_DISPLAY_MODE.CTA && (
            <fieldset>
              <p className='mb-2 font-medium text-sm text-gray-38'>CTA</p>
              <Input
                type='text'
                value={formState.cta}
                onChange={(e) =>
                  setFormState({
                    ...formState,
                    cta: e.target.value
                  })
                }
                className='mb-6'
                placeholder='CTA'
                inputClass='h-[50px]'
              />
            </fieldset>
          )}

          <div className='flex items-start w-full gap-4 mb-6'>
            <section className='flex flex-col gap-1.5 grow'>
              <p className='text-gray-38 text-sm font-medium'>Start date</p>
              <div
                className='border border-gray-19 rounded-md flex items-center  py-4 px-5 cursor-pointer h-[50px]'
                onClick={() => setShowStartDateModal(true)}>
                <CalendarIcon className='mr-2' />
                <p className='text-sm text-gray-6'>
                  {formState.startDate
                    ? moment(formState.startDate).format('YYYY-MM-DD')
                    : 'YYYY-MM-DD'}
                </p>
              </div>
            </section>
            <section className='flex flex-col gap-1.5 grow'>
              <p className='text-gray-38 text-sm font-medium'>Start time</p>
              <div className='border border-gray-19 rounded-md flex items-center py-4 px-5 h-[50px]'>
                <TimeIcon className='mr-2' />
                <TimeSelector
                  key={formState.startTime?.timeWithColon}
                  value={formState.startTime}
                  onChange={(value) => handleChange({ name: 'startTime', value })}
                />
              </div>
            </section>
          </div>
          {formState?.hasEndTime && (
            <div className='flex items-start w-full gap-4 mb-6'>
              <section className='flex flex-col gap-1.5 grow'>
                <p className='text-gray-38 text-sm font-medium'>End date</p>
                <div
                  className='border border-gray-19 rounded-md flex items-center  py-4 px-5 cursor-pointer h-[50px]'
                  onClick={() => setShowEndDateModal(true)}>
                  <CalendarIcon className='mr-2' />
                  <p className='text-sm text-gray-6'>
                    {formState.endDate
                      ? moment(formState.endDate).format('YYYY-MM-DD')
                      : 'YYYY-MM-DD'}
                  </p>
                </div>
              </section>
              <section className='flex flex-col gap-1.5 grow'>
                <p className='text-gray-38 text-sm font-medium'>End time</p>
                <div className='border border-gray-19 rounded-md flex items-center py-4 px-5 h-[50px]'>
                  <TimeIcon className='mr-2' />
                  <TimeSelector
                    key={formState.endTime?.timeWithColon}
                    value={formState.endTime}
                    onChange={(value) => handleChange({ name: 'endTime', value })}
                  />
                </div>
              </section>
            </div>
          )}
          <div className='flex items-center gap-4'>
            <p className='font-medium text-sm text-gray-38'>Set end date & time</p>
            <Switch
              active={formState.hasEndTime}
              onChange={(val) => handleChange({ name: 'hasEndTime', value: val })}
            />
          </div>

          <fieldset className='font-medium text-sm'>
            <p className='text-gray-38'>Is this a promo post?</p>
            <div className='flex gap-4 mt-4'>
              <Input
                type='radio'
                name='promo'
                id='promo-yes'
                label='Yes, it is'
                className='accent-red-5'
                checked={formState.promoPost}
                onChange={(e) => handleChange({ name: 'promoPost', value: e.target.checked })}
              />
              <Input
                id='promo-no'
                type='radio'
                name='promo'
                label="No, it isn't"
                className='accent-red-5'
                checked={!formState.promoPost}
                onChange={(e) => handleChange({ name: 'promoPost', value: !e.target.checked })}
              />
            </div>
          </fieldset>
          <div className='flex items-center justify-between'>
            <button className='bg-gray-36 rounded-full p-2' onClick={() => navigate(-1)}>
              <RmxArrowBackIcon className='text-gray-37' />
            </button>
            <Button
              onClick={handleSubmitExplorePost}
              loading={isUpdating || isMutating}
              color='primary'
              variant='block'
              size='small'
              disabled={!isFormValid}
              className='font-semibold w-auto'>
              Continue
            </Button>
          </div>
        </form>
      )}
    </section>
  );
};
export default ContentUpload;
